import { Dayjs } from "dayjs";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import TipJarGetBalanceResponse from "./TipJarResponses/TipJarGetBalanceResponse";
import TipJarGetBalanceForPeriodResponse from "./TipJarResponses/TipJarGetBalanceForPeriodResponse";
import TipJarGetTransactionsForPeriodResponse from "./TipJarResponses/TipJarGetTransactionsForPeriodResponse";
import TipJarTransactionResponse from "./TipJarResponses/TipJarTransactionResponse";
import TipJarGetOverviewResponse from "./TipJarResponses/TipJarGetOverviewResponse";
import { TipGotExpectedResponse } from "./TipJarResponses/TipGotExpectedResponse";
import { TipGetGotExpectedResponse } from "./TipJarResponses/TipGetGotExpectedResponse";

export default class TipJarRepository extends RepositoryBase {

    public static async getTipBalance(locationId: string): Promise<ResponseEnvelope<TipJarGetBalanceResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<TipJarGetBalanceResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/tipjar`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<TipJarGetBalanceResponse>(
                true,
                {
                    balance: data.balance
                } as TipJarGetBalanceResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getTransactionsForPeriod(locationId: string, startMonthData: Dayjs, endMonthDate: Dayjs): Promise<ResponseEnvelope<TipJarGetTransactionsForPeriodResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<TipJarGetTransactionsForPeriodResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/tipjar/transactions`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startMonth: startMonthData.format("YYYY-MM-DD"),
                    endMonth: endMonthDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<TipJarGetTransactionsForPeriodResponse>(
                true,
                {
                    transactions: data.transactions.map((x: any) => ({
                        id: x.id,
                        amount: x.amount,
                        description: x.description,
                        date: x.date,
                        createdBy: x.createdBy,
                        createdByName: x.createdByName,
                    } as TipJarTransactionResponse))
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getTransactionsOverview(locationId: string, startMonthData: Dayjs, endMonthDate: Dayjs): Promise<ResponseEnvelope<TipJarGetOverviewResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<TipJarGetOverviewResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/tipjar/transactions/overview`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startMonth: startMonthData.format("YYYY-MM-DD"),
                    endMonth: endMonthDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<TipJarGetOverviewResponse>(
                true,
                {
                    transactions: data.transactions
                } as TipJarGetOverviewResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getBalanceOverview(locationId: string, startMonthData: Dayjs, endMonthDate: Dayjs): Promise<ResponseEnvelope<TipJarGetOverviewResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<TipJarGetOverviewResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;
                        
            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/tipjar/overview`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startMonth: startMonthData.format("YYYY-MM-DD"),
                    endMonth: endMonthDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<TipJarGetOverviewResponse>(
                true,
                {
                    transactions: data.transactions
                } as TipJarGetOverviewResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async calculateGotExpected(locationId: string, startMonth: Dayjs, endMonth: Dayjs): Promise<ResponseEnvelope<TipGetGotExpectedResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<TipGetGotExpectedResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            // Build query params — GET requests cannot have a body
            const params = new URLSearchParams({
                locationId: locationId,
                startDate: startMonth.format("YYYY-MM-DD"),
                endDate: endMonth.format("YYYY-MM-DD"),
            });

            const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/tipjar/gotexpected?${params}`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<TipGetGotExpectedResponse>(
                true,
                {
                    gotExpected: data.gotExpected
                } as TipGetGotExpectedResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }
}